import moment from 'moment';

export const DASHBOARD_DATE_MODES = {
    TODAY: 'today',
    CURRENT_MONTH: 'current_month',
    LAST_TWO_MONTHS: 'last_two_months',
    DATE_RANGE: 'date_range',
};

export const DASHBOARD_DATE_RANGE_OPTIONS = [
    { value: DASHBOARD_DATE_MODES.TODAY, label: 'Today' },
    { value: DASHBOARD_DATE_MODES.CURRENT_MONTH, label: 'Current Month' },
    { value: DASHBOARD_DATE_MODES.LAST_TWO_MONTHS, label: 'Last Two Month' },
    { value: DASHBOARD_DATE_MODES.DATE_RANGE, label: 'Custom Range' },
];

export const DASHBOARD_TAB_OPTIONS = [
    { value: 'schedule', label: 'Schedule' },
    { value: 'sales', label: 'Sales' },
    { value: 'purchase', label: 'Purchase' },
    { value: 'qaqc', label: 'QAQC' },
    { value: 'stock', label: 'Stock (Live)' },
];

const STORAGE_PREFIX = 'dashboardSettings';

export function getDashboardSettingsStorageKey(userId) {
    return `${STORAGE_PREFIX}_${userId || 'default'}`;
}

export function getDefaultDashboardSettings() {
    const now = moment();
    return {
        plantIds: [],
        defaultPlantId: null,
        dateMode: DASHBOARD_DATE_MODES.CURRENT_MONTH,
        customFromDate: now.clone().startOf('month').format('DD-MM-YYYY'),
        customToDate: now.format('DD-MM-YYYY'),
        enabledTabs: DASHBOARD_TAB_OPTIONS.map((tab) => tab.value),
        autoRefresh: true,
        defaultTab: 'schedule',
    };
}

export function normalizeDisplayDate(value) {
    if (value == null || value === '') {
        return null;
    }
    if (moment.isMoment(value) && value.isValid()) {
        return value.format('DD-MM-YYYY');
    }
    const trimmed = String(value).trim();
    if (!trimmed) {
        return null;
    }
    const strict = moment(trimmed, 'DD-MM-YYYY', true);
    if (strict.isValid()) {
        return strict.format('DD-MM-YYYY');
    }
    return null;
}

export function sanitizeDashboardSettings(settings) {
    const defaults = getDefaultDashboardSettings();
    const next = {
        ...defaults,
        ...(settings || {}),
        plantIds: Array.isArray(settings?.plantIds) ? settings.plantIds : defaults.plantIds,
        enabledTabs: Array.isArray(settings?.enabledTabs) && settings.enabledTabs.length
            ? settings.enabledTabs.filter((tab) =>
                DASHBOARD_TAB_OPTIONS.some((option) => option.value === tab),
            )
            : defaults.enabledTabs,
    };

    const allowedModes = Object.values(DASHBOARD_DATE_MODES);
    if (!allowedModes.includes(next.dateMode)) {
        next.dateMode = defaults.dateMode;
    }

    if (!next.enabledTabs.includes(next.defaultTab)) {
        next.defaultTab = next.enabledTabs[0] || 'schedule';
    }

    next.customFromDate = normalizeDisplayDate(next.customFromDate) || defaults.customFromDate;
    next.customToDate = normalizeDisplayDate(next.customToDate) || defaults.customToDate;

    if (next.dateMode !== DASHBOARD_DATE_MODES.DATE_RANGE) {
        return next;
    }

    const fromMoment = moment(next.customFromDate, 'DD-MM-YYYY', true);
    const toMoment = moment(next.customToDate, 'DD-MM-YYYY', true);
    if (fromMoment.isAfter(toMoment)) {
        next.customToDate = next.customFromDate;
    }
    return next;
}

export function loadDashboardSettings(userId) {
    try {
        const raw = window.localStorage.getItem(getDashboardSettingsStorageKey(userId));
        if (!raw) {
            return getDefaultDashboardSettings();
        }
        return sanitizeDashboardSettings({
            ...getDefaultDashboardSettings(),
            ...JSON.parse(raw),
        });
    } catch (error) {
        return getDefaultDashboardSettings();
    }
}

export function saveDashboardSettings(userId, settings) {
    const payload = sanitizeDashboardSettings(settings);
    window.localStorage.setItem(
        getDashboardSettingsStorageKey(userId),
        JSON.stringify(payload),
    );
    window.dispatchEvent(new CustomEvent('dashboardSettingsUpdated', { detail: payload }));
    return payload;
}

export function getDateRangeFromDashboardSettings(settings) {
    const now = moment();
    switch (settings?.dateMode) {
        case DASHBOARD_DATE_MODES.TODAY:
            return [now.clone().startOf('day'), now.clone().endOf('day')];
        case DASHBOARD_DATE_MODES.LAST_TWO_MONTHS:
            return [
                now.clone().subtract(1, 'month').startOf('month'),
                now.clone().endOf('month'),
            ];
        case DASHBOARD_DATE_MODES.DATE_RANGE: {
            const from = normalizeDisplayDate(settings.customFromDate);
            const to = normalizeDisplayDate(settings.customToDate);
            if (from && to) {
                const fromMoment = moment(from, 'DD-MM-YYYY', true);
                const toMoment = moment(to, 'DD-MM-YYYY', true);
                if (fromMoment.isAfter(toMoment)) {
                    return [fromMoment, fromMoment];
                }
                return [fromMoment, toMoment];
            }
            return [now.clone().startOf('month'), now.clone().endOf('month')];
        }
        case DASHBOARD_DATE_MODES.CURRENT_MONTH:
        default:
            return [now.clone().startOf('month'), now.clone().endOf('month')];
    }
}

export function resolveDashboardPlants(settings, allowedPlants) {
    const allowedIdSet = new Set(allowedPlants.map((plant) => Number(plant.value)));
    const selectedIds = (settings.plantIds || [])
        .map((id) => Number(id))
        .filter((id) => allowedIdSet.has(id));

    const plants = selectedIds.length
        ? allowedPlants.filter((plant) => selectedIds.includes(Number(plant.value)))
        : allowedPlants;

    let defaultPlantId = settings.defaultPlantId;
    if (!defaultPlantId || !plants.some((plant) => Number(plant.value) === Number(defaultPlantId))) {
        defaultPlantId = plants[0]?.value ?? null;
    }

    return { plants, defaultPlantId };
}

export function getDashboardSettingsSummary(settings, allowedPlants) {
    const { plants, defaultPlantId } = resolveDashboardPlants(settings, allowedPlants);
    const [fromDate, toDate] = getDateRangeFromDashboardSettings(settings);
    const plantCount = settings.plantIds?.length;
    const plantLabel = !plantCount || plantCount === allowedPlants.length
        ? `All plants (${allowedPlants.length})`
        : `${plantCount} plant(s)`;
    const defaultPlant = plants.find((plant) => plant.value === Number(defaultPlantId));
    const defaultPlantLabel = defaultPlant?.label || '—';

    let dateLabel = 'Current Month';
    if (settings.dateMode === DASHBOARD_DATE_MODES.TODAY) {
        dateLabel = 'Today';
    } else if (settings.dateMode === DASHBOARD_DATE_MODES.LAST_TWO_MONTHS) {
        dateLabel = 'Last Two Month';
    } else if (settings.dateMode === DASHBOARD_DATE_MODES.DATE_RANGE) {
        dateLabel = `${fromDate.format('DD-MM-YYYY')} to ${toDate.format('DD-MM-YYYY')}`;
    }

    const tabCount = settings.enabledTabs?.length || DASHBOARD_TAB_OPTIONS.length;
    return `${defaultPlantLabel} · ${plantLabel} · ${dateLabel} · ${tabCount} tab(s)`;
}
